import { extrairProva, encurtarHash, rotuloDoTier, corDoTier, type EventoDeEvidencia } from '../lib/evidence';
import { humanizarEvento, corDoTom, rotuloDoTom } from '../lib/eventHumanizer';
import type { QueuedEvidence } from '../services/banQueue';

// ============================================================
// EvidenceLine — a prova de UM sinal, legível sem clicar
// ============================================================
// A tela de Events mostra o evento com a frase do humanizer em primeiro plano
// e o payload atrás de um `<details>`. Aqui a prova CONCRETA (arquivo + hashes,
// nome da DLL, nome do processo) sobe para a linha: esta tela existe para
// decidir um ban, e o operador não vai abrir 40 `<details>` para isso.
//
// DUAS ORIGENS, UMA LINHA:
//   • `/admin/ban-queue` devolve `QueuedEvidence` — tier vindo do MOTOR DE
//     RISCO e um mapa `proof` com whitelist fechada. Quando existe tier, ele
//     manda: é a mesma classificação que decide kick.
//   • `/admin/events` devolve o evento cru. Sem tier, o peso vem do `tom` do
//     humanizer, que o teste de contrato amarra aos mesmos tiers.
//
// Hash aparece truncado com o valor completo no `title` — 64 caracteres duas
// vezes por linha empurrariam tudo o mais para fora da vista, e o hash inteiro
// só é necessário quando alguém vai conferir contra o manifesto.
// ============================================================

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-red-900 text-red-300 border-red-700',
  high: 'bg-orange-900 text-orange-300 border-orange-700',
  suspect: 'bg-amber-900 text-amber-300 border-amber-700',
  info: 'bg-blue-900 text-blue-300 border-blue-700',
};

interface Props {
  /** Evento cru de `/admin/events`. */
  evento?: EventoDeEvidencia;
  /** Sinal agregado de `/admin/ban-queue`. */
  sinal?: QueuedEvidence;
}

export function EvidenceLine({ evento, sinal }: Props) {
  const eventType = sinal?.event_type ?? evento?.event_type ?? '';
  const dados: Record<string, unknown> | null | undefined =
    sinal ? sinal.proof : evento?.payload;

  const prova = extrairProva(eventType, dados);
  const severidade = sinal
    ? (sinal.severity_max >= 90 ? 'critical' : sinal.severity_max >= 70 ? 'high'
       : sinal.severity_max >= 30 ? 'suspect' : 'info')
    : evento?.severity_label;
  const repeticoes = sinal?.hits ?? evento?.occurrence_count;
  const quando = sinal?.last_seen ?? evento?.last_seen_at ?? evento?.created_at;

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
      {sinal ? (
        <span
          className={`px-1.5 py-0.5 rounded border shrink-0 ${corDoTier(sinal.tier)}`}
          title={`Tier do motor de risco · família "${sinal.family}" — duas famílias Strong ou uma Confirmed sustentam expulsão`}
        >
          {rotuloDoTier(sinal.tier)}
        </span>
      ) : (
        (() => {
          const h = humanizarEvento(eventType, evento?.payload);
          return (
            <span
              className={`px-1.5 py-0.5 rounded border shrink-0 ${corDoTom(h.tom)}`}
              title="Peso do sinal — 'alteração confirmada' é o único que expulsa sozinho"
            >
              {rotuloDoTom(h.tom)}
            </span>
          );
        })()
      )}

      {severidade && (
        <span
          className={`px-1.5 py-0.5 rounded border shrink-0 ${
            SEVERITY_COLOR[severidade] || 'bg-gray-800 text-gray-400 border-gray-700'
          }`}
        >
          {severidade}
        </span>
      )}

      {/* A PROVA. Hashes com valor completo no title. */}
      {prova.tipo === 'hash' && prova.esperado && prova.encontrado ? (
        <span className="text-gray-100">
          <strong className="text-gray-50">{prova.alvo}</strong>
          <span className="text-gray-500"> — esperado </span>
          <code className="text-emerald-300" title={prova.esperado}>
            {encurtarHash(prova.esperado)}
          </code>
          <span className="text-gray-500"> × encontrado </span>
          <code className="text-red-300" title={prova.encontrado}>
            {encurtarHash(prova.encontrado)}
          </code>
        </span>
      ) : (
        <span className="text-gray-100">{prova.linha}</span>
      )}

      {repeticoes !== undefined && repeticoes > 1 && (
        <span
          className="px-1.5 py-0.5 rounded bg-purple-900 text-purple-200 shrink-0"
          title="Ocorrências agregadas na janela"
        >
          ×{repeticoes}
        </span>
      )}

      <code className="text-gray-600 shrink-0">{eventType}</code>

      {quando && (
        <span className="text-gray-500 shrink-0 ml-auto" title={quando}>
          {new Date(quando).toLocaleString()}
        </span>
      )}
    </div>
  );
}
